import { RecallToothDisplayModelKey, RecallToothStatus } from '@app/shared/models';

export type ToothDisplayModelKey = RecallToothDisplayModelKey;

export interface ToothDisplayModel {
  key: ToothDisplayModelKey;
  label: string;
  surfaceCompatible: boolean;
  priority: number;
  codes: string[];
  terms: string[];
}

export const TOOTH_DISPLAY_MODELS: ToothDisplayModel[] = [
  {
    key: 'natural',
    label: 'Natural Tooth',
    surfaceCompatible: true,
    priority: 0,
    codes: [],
    terms: []
  },
  {
    key: 'missing',
    label: 'Missing Tooth',
    surfaceCompatible: false,
    priority: 100,
    codes: ['missing', 'extracted', 'absent'],
    terms: ['missing', 'extracted', 'absent']
  },
  {
    key: 'implant',
    label: 'Implant',
    surfaceCompatible: false,
    priority: 90,
    codes: ['implant'],
    terms: ['implant']
  },
  {
    key: 'impacted',
    label: 'Impacted Tooth',
    surfaceCompatible: false,
    priority: 80,
    codes: ['impacted'],
    terms: ['impacted']
  },
  {
    key: 'unerupted',
    label: 'Unerupted Tooth',
    surfaceCompatible: false,
    priority: 70,
    codes: ['unerupted'],
    terms: ['unerupted', 'un-erupted', 'un erupted']
  },
  {
    key: 'root-fragment',
    label: 'Root Fragment',
    surfaceCompatible: false,
    priority: 60,
    codes: ['root-fragment', 'rootfragment'],
    terms: ['root fragment', 'root-fragment', 'residual root', 'retained root']
  },
  {
    key: 'supernumerary',
    label: 'Supernumerary Tooth',
    surfaceCompatible: true,
    priority: 50,
    codes: ['supernumerary'],
    terms: ['supernumerary']
  }
];

export function getToothModelByKey(key: any): ToothDisplayModel | null {
  return TOOTH_DISPLAY_MODELS.find((model) => model.key === key) || null;
}

export function getNaturalToothModel(): ToothDisplayModel {
  return TOOTH_DISPLAY_MODELS[0];
}

export function resolveToothDisplayModel(tooth: Partial<RecallToothStatus> | null | undefined): ToothDisplayModel {
  const overrideModel = getToothModelByKey(tooth?.toothModelOverride);
  if (overrideModel) {
    return overrideModel;
  }
  if (!tooth?.legendsObj?.length) {
    return getNaturalToothModel();
  }
  const candidates = tooth.legendsObj
    .map((legend) => findToothDisplayModel(legend))
    .filter((model): model is ToothDisplayModel => !!model)
    .sort((a, b) => b.priority - a.priority);

  return candidates[0] || getNaturalToothModel();
}

function findToothDisplayModel(legend: any): ToothDisplayModel | null {
  const code = normalizeModelToken(legend?.cd || legend?.code);
  const name = normalizeModelText(legend?.t || legend?.name);
  return TOOTH_DISPLAY_MODELS.find((model) => {
    if (model.key === 'natural') {
      return false;
    }
    const codeMatch = code && model.codes.some((item) => normalizeModelToken(item) === code);
    const termMatch = name && model.terms.some((term) => name.indexOf(normalizeModelText(term)) >= 0);
    return codeMatch || termMatch;
  }) || null;
}

function normalizeModelToken(value: any): string {
  return normalizeModelText(value).replace(/[^a-z0-9]/g, '');
}

function normalizeModelText(value: any): string {
  return String(value || '').trim().toLowerCase();
}
